import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";

export async function updateAutoAlertsSetup({ locationId, autoAlertsSetupId, input }) {
	const query = gql`
		mutation updateAutoAlertsSetup($locationId: String!, $autoAlertsSetupId: String!, $input: AutoAlertsSetupInput!) {
			updateAutoAlertsSetup(locationId: $locationId, autoAlertsSetupId: $autoAlertsSetupId, input: $input) {
				_id
				title
				createdOn
				itemsCount
				lastModifiedOn
				effectiveDateTo
				effectiveDateFrom
				items {
					_id
					activityType {
						_id
						description
					}
					actionType {
						_id
						description
						customerInitiated
					}
					alertDate
					initiatedBy
					associationWindow
					commission {
						input
						value
					}
					createdAt
					createdBy {
						_id
						fullName
					}
					lastModifiedOn
				}
			}
		}
	`;

	const variables = {
		locationId,
		autoAlertsSetupId,
		input,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateAutoAlertsSetup;
	} catch (err) {
		errorLogger(err, {
			function: "updateAutoAlertsSetup",
			message: "Error updating auto alerts setup",
			variables,
		});
		return null;
	}
}

export async function updateAutoAlertsSetupTitle({ locationId, listId, title }) {
	const query = gql`
		mutation updateAutoAlertsSetup($locationId: String!, $autoAlertsSetupId: String!, $input: AutoAlertsSetupInput!) {
			updateAutoAlertsSetup(locationId: $locationId, autoAlertsSetupId: $autoAlertsSetupId, input: $input) {
				_id
				title
				createdOn
				itemsCount
				lastModifiedOn
				effectiveDateTo
				effectiveDateFrom
				items {
					_id
					activityType {
						_id
						description
					}
					actionType {
						_id
						description
						customerInitiated
					}
					alertDate
					initiatedBy
					associationWindow
					commission {
						input
						value
					}
					createdAt
					createdBy {
						_id
						fullName
					}
					lastModifiedOn
				}
			}
		}
	`;

	const variables = {
		locationId,
		autoAlertsSetupId: listId,
		input: {
			title,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateAutoAlertsSetup;
	} catch (err) {
		errorLogger(err, {
			function: "updateAutoAlertsSetupTitle",
			message: "Error updating auto alerts setup title",
			variables,
		});
		return null;
	}
}

export async function updateAutoAlertsEffectiveDate({ locationId, autoAlertsSetupId, effectiveDate, effectiveDateValue }) {
	const query = gql`
		mutation updateAutoAlertsSetup($locationId: String!, $autoAlertsSetupId: String!, $input: AutoAlertsSetupInput!) {
			updateAutoAlertsSetup(locationId: $locationId, autoAlertsSetupId: $autoAlertsSetupId, input: $input) {
				_id
				title
				itemsCount
				createdOn
				lastModifiedOn
				effectiveDateFrom
				effectiveDateTo
				items {
					_id
					alertDate
					createdAt
					createdBy {
						fullName
					}
					actionType {
						_id
						description
						customerInitiated
					}
					activityType {
						_id
						description
					}
					associationWindow
					commission {
						input
						value
					}
					createdAt
					initiatedBy
					lastModifiedOn
				}
			}
		}
	`;

	const variables = {
		locationId,
		autoAlertsSetupId,
		input: {
			[effectiveDate]: effectiveDateValue,
		},
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateAutoAlertsSetup;
	} catch (err) {
		errorLogger(err, {
			function: "updateAutoAlertsEffectiveDate",
			message: "Error updating auto alerts effective date",
			variables,
		});
		return null;
	}
}

export async function deleteAutoAlertsSetup({ locationId, autoAlertsSetupId, commissionCycleId }) {
	const query = gql`
		mutation deleteAutoAlertsSetup($locationId: String!, $autoAlertsSetupId: String!, $commissionCycleId: String) {
			deleteAutoAlertsSetup(locationId: $locationId, autoAlertsSetupId: $autoAlertsSetupId, commissionCycleId: $commissionCycleId) {
				commissionCycleId
				_id
			}
		}
	`;

	const variables = {
		locationId,
		autoAlertsSetupId,
		commissionCycleId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteAutoAlertsSetup;
	} catch (err) {
		errorLogger(err, { function: "deleteAutoAlertsSetup", message: "Error deleting auto alerts setup", variables });
		return null;
	}
}

export async function addAutoAlertsSetup({ locationId, commissionCycleId, input }) {
	const query = gql`
		mutation createAutoAlertsSetup($locationId: String!, $commissionCycleId: String!, $input: CreateSetupInput!) {
			createAutoAlertsSetup(locationId: $locationId, commissionCycleId: $commissionCycleId, input: $input) {
				_id
				commissionCycleId
				createdOn
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				title
				items {
					_id
					alertDate
					createdAt
					createdBy {
						fullName
					}
					actionType {
						_id
						description
						customerInitiated
					}
					activityType {
						_id
						description
					}
					associationWindow
					commission {
						input
						value
					}
					createdAt
					initiatedBy
					lastModifiedOn
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		input,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.createAutoAlertsSetup;
	} catch (err) {
		errorLogger(err, { function: "addAutoAlertsSetup", message: "Error adding auto alerts setup", variables });
		return null;
	}
}

export async function addAutoAlertItem({ locationId, commissionCycleId, autoAlertsSetupId, row }) {
	const input = {
		_id: row._id,
		activityType: row.activityType,
		actionType: row.actionType,
		initiatedBy: row.initiatedBy,
		alertDate: row.alertDate,
		associationWindow: row.associationWindow,
		commission: row.commission,
		createdBy: row.createdBy,
		createAt: row.createdAt,
	};

	const query = gql`
		mutation addAutoAlertItem(
			$locationId: String!
			$commissionCycleId: String!
			$autoAlertsSetupId: String!
			$input: AutoAlertItemInput!
		) {
			addAutoAlertItem(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				autoAlertsSetupId: $autoAlertsSetupId
				input: $input
			) {
				_id
				title
				items {
					_id
					activityType {
						_id
						description
					}
					actionType {
						_id
						description
						customerInitiated
					}
					commission {
						input
						value
					}
					createdBy {
						_id
						fullName
					}
					initiatedBy
					alertDate
					associationWindow
					createdAt
				}

				effectiveDateFrom
				effectiveDateTo
				itemsCount
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				createdOn
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		autoAlertsSetupId,
		input,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.addAutoAlertItem;
	} catch (err) {
		errorLogger(err, { function: "addAutoAlertItem", message: "Error adding auto alert item", variables });
		return null;
	}
}

export async function deleteAutoAlertItems({ commissionCycleId, itemIds, locationId, setupId }) {
	const query = gql`
		mutation deleteAutoAlertItems($commissionCycleId: String, $itemIds: [String], $locationId: String!, $setupId: String!) {
			deleteAutoAlertItems(locationId: $locationId, setupId: $setupId, commissionCycleId: $commissionCycleId, itemIds: $itemIds) {
				_id
				title
				items {
					_id
					activityType {
						_id
						description
					}
					actionType {
						_id
						description
						customerInitiated
					}
					commission {
						input
						value
					}
					createdBy {
						_id
						fullName
					}
					initiatedBy
					alertDate
					associationWindow
					createdAt
				}
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				createdOn
			}
		}
	`;

	const variables = {
		locationId,
		setupId,
		itemIds,
		commissionCycleId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.deleteAutoAlertItems;
	} catch (err) {
		errorLogger(err, { function: "deleteAutoAlertItems", message: "Error deleting auto alert items", variables });
		return null;
	}
}

export async function updateAutoAlertItem({ locationId, commissionCycleId, input, userName }) {
	const query = gql`
		mutation updateAutoAlertItem($locationId: String!, $commissionCycleId: String!, $input: AutoAlertItemInput!, $userName: String!) {
			updateAutoAlertItem(locationId: $locationId, commissionCycleId: $commissionCycleId, input: $input, userName: $userName) {
				_id
				title
				items {
					_id
					activityType {
						_id
						description
					}
					actionType {
						_id
						description
						customerInitiated
					}
					commission {
						input
						value
					}
					createdBy {
						_id
						fullName
					}
					initiatedBy
					alertDate
					associationWindow
					createdAt
				}
				effectiveDateFrom
				effectiveDateTo
				itemsCount
				lastModifiedBy {
					_id
					fullName
				}
				createdBy {
					_id
					fullName
				}
				lastModifiedOn
				createdOn
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		input,
		userName,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.updateAutoAlertItem;
	} catch (err) {
		errorLogger(err, { function: "updateAutoAlertItem", message: "Error updating auto alert item", variables });
		return null;
	}
}
